import React, { useMemo } from 'react';
import type {
  LeaveReportResponse,
  LeaveReportGroup,
  LeaveReportRow,
  LeaveReportCell,
  LeaveTypeInfo,
  ReportColumn,
} from '../../api/report.api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Render a cell's content as colored pills — one per leave type + holidays.
 */
function CellPills({ cell }: { cell: LeaveReportCell }) {
  return (
    <div className="rp-leave-report-table__pills">
      {cell.entries.map((entry) => (
        <span
          key={entry.abbreviation}
          className="rp-leave-report-table__pill"
          style={{ backgroundColor: entry.color }}
        >
          {entry.abbreviation} {entry.days}
        </span>
      ))}
      {cell.holidayDays > 0 && (
        <span className="rp-leave-report-table__pill rp-leave-report-table__pill--holiday">
          H {cell.holidayDays}
        </span>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: LeaveReportResponse;
}

const LeaveReportTable: React.FC<Props> = ({ data }) => {
  const { columns, groups, leaveTypes } = data;

  // Number of fixed columns: Team/Person + Total days + H days + per leave type
  const fixedColCount = 3 + leaveTypes.length;

  return (
    <div className="rp-report-table__wrapper">
      <div className="rp-report-table__scroll">
        <table className="rp-report-table rp-leave-report-table">
          <thead>
            <tr>
              <th className="rp-report-table__header-fixed rp-leave-report-table__col-person">
                Team / Person
              </th>
              <th className="rp-report-table__header-fixed rp-leave-report-table__col-number">
                Total, days
              </th>
              <th className="rp-report-table__header-fixed rp-leave-report-table__col-number">
                H, days
              </th>
              {leaveTypes.map((lt) => (
                <th
                  key={lt.id}
                  className="rp-report-table__header-fixed rp-leave-report-table__col-number"
                  title={lt.name}
                >
                  {lt.abbreviation}, days
                </th>
              ))}
              {columns.map((col) => (
                <th key={col.key} className="rp-leave-report-table__col-header">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <LeaveGroupRows
                key={group.groupId}
                group={group}
                columns={columns}
                leaveTypes={leaveTypes}
                showGroupHeader={groups.length > 1 || group.groupName !== 'All Users'}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="rp-leave-report-table__legend">
        {leaveTypes.map((lt) => (
          <div key={lt.id} className="rp-leave-report-table__legend-item">
            <span
              className="rp-leave-report-table__legend-swatch"
              style={{ backgroundColor: lt.color }}
            />
            <span>
              {lt.abbreviation} &mdash; {lt.name}
            </span>
          </div>
        ))}
        <div className="rp-leave-report-table__legend-item">
          <span
            className="rp-leave-report-table__legend-swatch"
            style={{ backgroundColor: '#676879' }}
          />
          <span>H &mdash; Public Holiday</span>
        </div>
      </div>

      {/* Report description */}
      <div className="rp-report-description">
        <div className="rp-report-description__title">How to read this report</div>
        <p className="rp-report-description__text">
          This report provides a <strong>leave and holiday summary</strong> per person across the selected time period.
          The summary columns on the left show totals, while time-period columns on the right show leave details per scope interval.
          Only <strong>approved</strong> leave requests are included — pending, rejected, and cancelled requests are excluded.
        </p>
        <div className="rp-report-description__subtitle">Column definitions</div>
        <ul className="rp-report-description__list">
          <li>
            <strong>Total, days</strong> &mdash; sum of all leave days (full days count as 1, half-days count as 0.5).
          </li>
          <li>
            <strong>H, days</strong> &mdash; public holiday days falling within the period, based on the assigned holiday calendar scheme.
          </li>
          <li>
            <strong>Leave type columns</strong> (e.g. AL, SL) &mdash; total days per leave type. Leave types and their abbreviations are defined by your workspace admin.
          </li>
        </ul>
        <div className="rp-report-description__subtitle">Cell format</div>
        <p className="rp-report-description__text">
          Period cells display colored pills for each leave type (e.g. <code>VA 5</code> <code>SI 3</code> <code>H 2</code>),
          showing the abbreviation and number of days. Each pill is color-coded to match the leave type.
          Holiday days are shown with a neutral &ldquo;H&rdquo; pill.
        </p>
      </div>
    </div>
  );
};

// ─── Group rows ──────────────────────────────────────────────────────────────

const LeaveGroupRows: React.FC<{
  group: LeaveReportGroup;
  columns: ReportColumn[];
  leaveTypes: LeaveTypeInfo[];
  showGroupHeader: boolean;
}> = ({ group, columns, leaveTypes, showGroupHeader }) => {
  return (
    <>
      {showGroupHeader && (
        <tr className="rp-report-table__group-row">
          <td className="rp-report-table__header-fixed rp-leave-report-table__col-person">
            <strong>{group.groupName}</strong>
          </td>
          <td className="rp-report-table__header-fixed rp-leave-report-table__col-number" />
          <td className="rp-report-table__header-fixed rp-leave-report-table__col-number" />
          {leaveTypes.map((lt) => (
            <td
              key={lt.id}
              className="rp-report-table__header-fixed rp-leave-report-table__col-number"
            />
          ))}
          {columns.map((col) => (
            <td key={col.key} className="rp-leave-report-table__cell" />
          ))}
        </tr>
      )}
      {group.rows.map((row) => (
        <LeaveUserRow
          key={row.userId}
          row={row}
          columns={columns}
          leaveTypes={leaveTypes}
        />
      ))}
    </>
  );
};

// ─── User row ────────────────────────────────────────────────────────────────

const LeaveUserRow: React.FC<{
  row: LeaveReportRow;
  columns: ReportColumn[];
  leaveTypes: LeaveTypeInfo[];
}> = ({ row, columns, leaveTypes }) => {
  const cellMap = useMemo(() => {
    const map = new Map<string, LeaveReportCell>();
    for (const cell of row.cells) {
      map.set(cell.startDate, cell);
    }
    return map;
  }, [row.cells]);

  return (
    <tr className="rp-report-table__user-row">
      <td className="rp-report-table__header-fixed rp-leave-report-table__col-person">
        {row.userName}
      </td>
      <td className="rp-report-table__header-fixed rp-leave-report-table__col-number">
        {row.totalDays}
      </td>
      <td className="rp-report-table__header-fixed rp-leave-report-table__col-number">
        {row.holidayDays}
      </td>
      {leaveTypes.map((lt) => {
        const typeSummary = row.leaveTypeDays.find(
          (td) => td.leaveTypeId === lt.id,
        );
        const days = typeSummary?.days ?? 0;
        return (
          <td
            key={lt.id}
            className="rp-report-table__header-fixed rp-leave-report-table__col-number"
          >
            {days > 0 ? days : ''}
          </td>
        );
      })}
      {columns.map((col) => {
        const cell = cellMap.get(col.startDate);
        if (!cell || (cell.entries.length === 0 && !cell.holidayDays)) {
          return (
            <td key={col.key} className="rp-leave-report-table__cell" />
          );
        }
        return (
          <td key={col.key} className="rp-leave-report-table__cell rp-leave-report-table__cell--filled">
            <CellPills cell={cell} />
          </td>
        );
      })}
    </tr>
  );
};

export default LeaveReportTable;
